import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, ArrowUpRight, Building2 } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const RevenueTrend = ({ historicalData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  if (!historicalData || historicalData.length === 0) return null;

  const sorted = [...historicalData].sort((a, b) => a.year - b.year);
  const maxRevenue = Math.max(...sorted.map(d => d.revenue));
  
  const firstYear = sorted[0];
  const lastYear = sorted[sorted.length - 1];
  const totalGrowthPercent = ((lastYear.revenue - firstYear.revenue) / firstYear.revenue * 100).toFixed(1);
  const totalGrowthAbsolute = lastYear.revenue - firstYear.revenue;

  // Berechne organisches Wachstum ohne Sonderspenden
  const lastYearRegular = lastYear.revenue - (lastYear.special_donations || 0);
  const organicGrowthPercent = ((lastYearRegular - firstYear.revenue) / firstYear.revenue * 100).toFixed(1);

  return (
    <section 
      ref={sectionRef} 
      id="trend" 
      data-testid="revenue-trend-section"
      className="py-24 px-6 bg-black border-t border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_right,rgba(255,70,0,0.5),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#FF4600]"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
              Einnahmen-Entwicklung
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-none">
              Der<br />
              <span className="text-[#FF4600]">Trend</span> ist klar
            </h2>
            <div className="lg:text-right">
              <div className="flex items-center gap-2 lg:justify-end mb-2">
                <TrendingUp className="w-6 h-6 text-[#FF4600]" strokeWidth={2.5} />
                <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
                  Wachstum seit {firstYear.year}
                </span>
              </div>
              <p className="text-5xl md:text-6xl font-black text-white">
                +{isVisible && <AnimatedCounter end={parseFloat(totalGrowthPercent)} duration={2000} decimals={1} />}%
              </p>
              <p className="text-white/60 mt-2">
                +{totalGrowthAbsolute.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </p>
            </div>
          </div>
        </div>

        {/* Trend Chart - With Stacked Bar for Special Donations */}
        <div className="bg-white/5 border border-white/10 p-8 md:p-12 mb-8">
          {/* Legend */}
          <div className="flex flex-wrap gap-6 mb-8 justify-center md:justify-end">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-t from-[#FF4600] to-[#ff6b2c]"></div>
              <span className="text-xs font-bold uppercase tracking-widest text-white/80">
                Reguläre Einnahmen
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-t from-[#FFA500] to-[#FFD700] relative">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.2) 2px, rgba(0,0,0,0.2) 4px)'
                }}></div>
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-white/80 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                Sonderspenden Gebäude
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 md:gap-12 items-end h-80 md:h-96">
            {sorted.map((data, index) => {
              const heightPercent = (data.revenue / maxRevenue) * 100;
              const previousYear = index > 0 ? sorted[index - 1] : null;
              const growthPercent = previousYear 
                ? ((data.revenue - previousYear.revenue) / previousYear.revenue * 100).toFixed(1)
                : null;
              const isLatest = index === sorted.length - 1;
              
              // Calculate split for special donations
              const specialDonations = data.special_donations || 0;
              const regularRevenue = data.revenue - specialDonations;
              const specialPercent = data.revenue > 0 ? (specialDonations / data.revenue) * 100 : 0;

              return (
                <div 
                  key={data.year} 
                  data-testid={`trend-bar-${data.year}`}
                  className="flex flex-col items-center h-full relative"
                >
                  {/* Growth indicator */}
                  {growthPercent && (
                    <div 
                      className={`absolute -top-2 left-1/2 -translate-x-1/2 transition-all duration-700 z-10 ${
                        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                      }`}
                      style={{ transitionDelay: `${index * 200 + 1500}ms` }}
                    >
                      <div className="bg-[#FF4600] text-white text-xs font-black uppercase tracking-widest px-3 py-1 whitespace-nowrap flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3" strokeWidth={3} />
                        +{growthPercent}%
                      </div>
                    </div>
                  )}

                  {/* Bar */}
                  <div className="flex-1 flex items-end w-full max-w-[200px]">
                    <div className="w-full relative">
                      <div
                        className="w-full transition-all duration-1500 ease-out relative overflow-hidden"
                        style={{
                          height: isVisible ? `${heightPercent * 2.5}px` : '0px',
                          maxHeight: '280px',
                          transitionDelay: `${index * 200}ms`,
                          minHeight: isVisible ? '60px' : '0px'
                        }}
                      >
                        {/* Regular revenue portion (bottom) */}
                        <div
                          className={`absolute bottom-0 left-0 right-0 ${
                            isLatest 
                              ? 'bg-gradient-to-t from-[#FF4600] to-[#ff6b2c]' 
                              : 'bg-gradient-to-t from-white/20 to-white/10'
                          }`}
                          style={{
                            height: `${100 - specialPercent}%`,
                          }}
                        ></div>
                        
                        {/* Special donations portion (top) - only for 2025 */}
                        {specialDonations > 0 && (
                          <div
                            className={`absolute top-0 left-0 right-0 transition-all duration-700 ${
                              isVisible ? 'opacity-100' : 'opacity-0'
                            }`}
                            style={{
                              height: `${specialPercent}%`,
                              transitionDelay: `${index * 200 + 1200}ms`,
                              background: 'linear-gradient(to top, #FFA500, #FFD700)',
                            }}
                          >
                            {/* Diagonal pattern overlay */}
                            <div 
                              className="absolute inset-0" 
                              style={{
                                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.15) 4px, rgba(0,0,0,0.15) 8px)'
                              }}
                            ></div>
                            {/* Building icon */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-black/40" strokeWidth={2.5} />
                            </div>
                          </div>
                        )}

                        {/* Revenue value at top of bar */}
                        <div 
                          className={`absolute -top-12 left-1/2 -translate-x-1/2 transition-all duration-700 ${
                            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                          }`}
                          style={{ transitionDelay: `${index * 200 + 1000}ms` }}
                        >
                          <p className={`text-lg md:text-2xl font-black whitespace-nowrap ${
                            isLatest ? 'text-[#FF4600]' : 'text-white'
                          }`}>
                            {data.revenue.toLocaleString('de-DE', { 
                              minimumFractionDigits: 0, 
                              maximumFractionDigits: 0 
                            })} €
                          </p>
                        </div>

                        {/* Special donations label - inside or beside bar */}
                        {specialDonations > 0 && (
                          <div 
                            className={`absolute left-full ml-3 transition-all duration-700 hidden md:block ${
                              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                            }`}
                            style={{ 
                              top: `${specialPercent / 2}%`,
                              transform: 'translateY(-50%)',
                              transitionDelay: `${index * 200 + 1400}ms`
                            }}
                          >
                            <div className="bg-[#FFD700] text-black px-2 py-1 text-xs font-black whitespace-nowrap">
                              +{specialDonations.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Year label */}
                  <div className="mt-4 text-center">
                    <p className={`text-2xl md:text-3xl font-black ${
                      isLatest ? 'text-[#FF4600]' : 'text-white/60'
                    }`}>
                      {data.year}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Note about special donations */}
        {lastYear.special_donations > 0 && (
          <div className={`mb-12 p-6 bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-start gap-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '2000ms' }}
          >
            <Building2 className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-[#FFD700] mb-1">
                Hinweis · Sonderspenden
              </p>
              <p className="text-white text-base md:text-lg font-light">
                In den Einnahmen {lastYear.year} sind <span className="font-bold text-[#FFD700]">
                {lastYear.special_donations.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                </span> einmalige Sonderspenden für das Gebäude enthalten. 
                Das organische Wachstum gegenüber {firstYear.year} beträgt <span className="font-bold text-white">+{organicGrowthPercent}%</span>.
              </p>
            </div>
          </div>
        )}

        {/* Year by Year Comparison */}
        <div className="grid md:grid-cols-3 gap-4">
          {sorted.map((data, index) => {
            const previousYear = index > 0 ? sorted[index - 1] : null;
            const revenueGrowth = previousYear 
              ? ((data.revenue - previousYear.revenue) / previousYear.revenue * 100).toFixed(1)
              : null;
            const isLatest = index === sorted.length - 1;
            const resultPositive = data.result >= 0;
            const specialDonations = data.special_donations || 0;

            return (
              <div 
                key={data.year}
                data-testid={`year-summary-${data.year}`}
                className={`p-6 border transition-all duration-700 ${
                  isLatest 
                    ? 'bg-[#FF4600] border-[#FF4600] text-white' 
                    : 'bg-white/5 border-white/10 text-white'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 150 + 800}ms` }}
              >
                <div className="flex justify-between items-start mb-4">
                  <p className="text-3xl font-black">{data.year}</p>
                  {isLatest && (
                    <span className="bg-white text-[#FF4600] text-xs font-black uppercase tracking-widest px-2 py-1">
                      Aktuell
                    </span>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className={`text-xs uppercase tracking-widest font-bold mb-1 ${
                      isLatest ? 'text-white/80' : 'text-[#FF4600]'
                    }`}>
                      Einnahmen
                    </p>
                    <p className="text-2xl font-black">
                      {data.revenue.toLocaleString('de-DE', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })} €
                    </p>
                    {revenueGrowth && (
                      <p className={`text-sm font-bold mt-1 flex items-center gap-1 ${
                        isLatest ? 'text-white' : 'text-[#FF4600]'
                      }`}>
                        <TrendingUp className="w-4 h-4" strokeWidth={3} />
                        +{revenueGrowth}% vs. {previousYear.year}
                      </p>
                    )}
                    {specialDonations > 0 && (
                      <p className={`text-xs mt-2 flex items-center gap-1 ${
                        isLatest ? 'text-white/90' : 'text-[#FFD700]'
                      }`}>
                        <Building2 className="w-3 h-3" />
                        davon {specialDonations.toLocaleString('de-DE', { maximumFractionDigits: 0 })} € Sonderspenden
                      </p>
                    )}
                  </div>

                  <div className={`pt-3 border-t ${isLatest ? 'border-white/20' : 'border-white/10'}`}>
                    <p className={`text-xs uppercase tracking-widest font-bold mb-1 ${
                      isLatest ? 'text-white/80' : 'text-white/60'
                    }`}>
                      Ergebnis
                    </p>
                    <p className={`text-xl font-black ${
                      isLatest ? 'text-white' : resultPositive ? 'text-[#FF4600]' : 'text-white/80'
                    }`}>
                      {resultPositive ? '+' : ''}{data.result.toLocaleString('de-DE', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })} €
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Key Insight */}
        <div className="mt-12 p-8 bg-gradient-to-r from-[#FF4600]/20 to-transparent border-l-4 border-[#FF4600]">
          <p className="text-xs uppercase tracking-widest font-bold text-[#FF4600] mb-2">
            Insight
          </p>
          <p className="text-2xl md:text-3xl font-black text-white leading-tight">
            Erstmals seit {firstYear.year} ein <span className="text-[#FF4600]">positives Ergebnis</span> – 
            mit <span className="text-white">+{organicGrowthPercent}%</span> organischem Wachstum 
            und einmaligen Sonderspenden fürs Gebäude.
          </p>
        </div>
      </div>
    </section>
  );
};

export default RevenueTrend;
